import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, cartApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { withClickSpan } from '@/lib/clickSpan';
import { toast } from '@/components/ui/sonner';

export function Login() {
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const qc = useQueryClient();

  const loginMut = useMutation({
    // Login flow: catalog overwrites the `session` cookie with the user's
    // UUID, which orphans the anonymous cart in cart-service. Snapshot the
    // anon cart, log in, then replay the items so the user sees what they
    // had pre-login.
    mutationFn: async () => {
      let snapshot: Array<{ productId: string; quantity: number }> = [];
      try {
        const cart = (await cartApi.get()).data;
        snapshot = cart.items.map((i) => ({ productId: i.productId, quantity: i.quantity }));
      } catch {
        snapshot = [];
      }

      const res = await authApi.login(email);

      for (const item of snapshot) {
        try {
          await cartApi.add(item.productId, item.quantity);
        } catch {
          // Best-effort: a single failed item shouldn't block login.
        }
      }
      return res;
    },
    onSuccess: (res) => {
      qc.setQueryData(['auth', 'me'], res.data);
      qc.invalidateQueries({ queryKey: ['cart'] });
      toast.success(`Welcome, ${res.data.email}`);
      navigate(next);
    },
    onError: (err: Error & { traceId?: string | null }) => {
      toast.error('Login failed', { description: err.traceId ?? err.message });
    },
  });

  return (
    <div className="max-w-md mx-auto space-y-6 mt-8">
      <h1 className="font-display text-3xl text-center">Sign in by Owl</h1>
      <p className="text-center text-(--color-ink-muted)">
        No password — drop us your email and the owl is on its way.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return;
          void withClickSpan('login-submit', { 'app.user.email_domain': email.split('@')[1] ?? '' }, () =>
            loginMut.mutateAsync(),
          );
        }}
        className="space-y-4"
      >
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hermione@hogwarts.edu"
          />
        </div>
        <Button type="submit" variant="cta" size="lg" className="w-full" disabled={loginMut.isPending}>
          {loginMut.isPending ? 'Sending owl…' : 'Send me an Owl'}
        </Button>
      </form>
    </div>
  );
}
